import type { ComponentType } from "react";
import type { MessageDescriptor } from "@lingui/core";
import type { AppMode } from "@/contexts/AppModeContext";
import type { ClientFeatureKey } from "@/hooks/useFeatures";

export interface NavItemConfig {
  title: MessageDescriptor;
  /**
   * Omitted for a group header, which expands its children rather than
   * navigating. Every leaf needs one.
   */
  url?: string;
  icon: ComponentType<{ className?: string }>;
  permission?: string | string[];
  disabled?: boolean;
  external?: boolean;
  /**
   * Runs something instead of navigating. `support-chat` opens the support
   * chat window (the widget's own floating launcher is hidden, YC-34).
   */
  action?: "support-chat";
  /** Short text shown as a badge next to the nav label (e.g. "NEW") */
  badge?: string;
  /** Which app modes this item appears in. Omit to show in all modes. */
  modes?: AppMode[];
  /**
   * A feature the caller's organization must have for this item to appear
   * (T-1106). Distinct from `permission`: a permission says what this user may
   * do, a feature says what this tenant bought.
   */
  feature?: ClientFeatureKey;
  /**
   * Child items. Present makes this a group: the sidebar renders it as an
   * expandable header instead of a link. One level only — the rail has no room
   * to show depth, and the target IA does not ask for more.
   */
  items?: NavItemConfig[];
}

export interface NavSectionConfig {
  title: MessageDescriptor;
  internal?: boolean;
  rolesAny?: string[];
  /** Which app modes this section appears in. Omit to show in all modes. */
  modes?: AppMode[];
  /**
   * When true the section is hidden for users who have no active organization
   * context (e.g. a super-admin who has not yet "logged in as" an org).
   */
  requiresOrgContext?: boolean;
  items: NavItemConfig[];
}

export class NavBuilder {
  private sections: NavSectionConfig[] = [];
  private current: NavSectionConfig | null = null;

  addSection(title: MessageDescriptor, opts?: { internal?: boolean; rolesAny?: string[]; modes?: AppMode[]; requiresOrgContext?: boolean }): this {
    this.current = { title, internal: opts?.internal, rolesAny: opts?.rolesAny, modes: opts?.modes, requiresOrgContext: opts?.requiresOrgContext, items: [] };
    this.sections.push(this.current);
    return this;
  }

  addItem(item: NavItemConfig): this {
    if (!this.current) throw new Error("Call addSection() before addItem()");
    this.current.items.push(item);
    return this;
  }

  build(): NavSectionConfig[] {
    return this.sections;
  }
}

export function createNavMenu(): NavBuilder {
  return new NavBuilder();
}
